import { useCallback, useEffect } from 'react';
import { DrawerToggle } from '../model/DrawerToggle';
import { DrawerToggleOptions } from '../model/DrawerToggleOptions';
import { useToggle } from './useToggle';

export const useDrawerToggle = (
  initial = false,
  { onToggle, onOpen, onClose, unmountDelayMillis = 500 }: DrawerToggleOptions = {},
): DrawerToggle => {
  const { isOn: isOpen, setOn: setOpen, setOff: setClosed } = useToggle();

  const {
    isOn: isMounted,
    setOn: setMounted,
    setOff: setUnmounted,
  } = useToggle(initial, {
    onOn: onOpen,
    onOff: onClose,
    onToggle,
  });

  useEffect(() => {
    /**
     * open/close animation does not work if drawer is opened on the same render as
     * it is mounted (i.e. it cannot be mounted in opened state for animation to work).
     * useEffect to split the mounting and opening into 2 renders.
     **/
    if (isMounted) {
      setOpen();
    }
  }, [isMounted, setOpen]);

  const open = useCallback(() => {
    setMounted();
  }, [setMounted]);

  const closeAsync = useCallback(async () => {
    setClosed();
    await new Promise(resolve => setTimeout(resolve, unmountDelayMillis));
    setUnmounted();
  }, [setClosed, setUnmounted, unmountDelayMillis]);

  const close = useCallback((): void => {
    closeAsync().catch(() => {});
  }, [closeAsync]);

  const toggleAsync = useCallback(async () => (isOpen ? await closeAsync() : open()), [open, closeAsync, isOpen]);

  const toggle = useCallback((): void => {
    toggleAsync().catch(() => {});
  }, [toggleAsync]);

  return {
    isOpen,
    isClosed: !isOpen,
    isMounted,
    open,
    closeAsync,
    close,
    toggleAsync,
    toggle,
  };
};
